var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import { DateAdapter } from '@angular/material';
import { addDays, addMonths, addYears, format, getDate, getDaysInMonth, getMonth, getYear, parse, setDate, setDay, setMonth, setYear, toDate, } from 'date-fns';
import { LocalizedDateFns } from './localized-date-fns.service';
export var MAT_DATEFNS_DATE_FORMATS = {
    parse: {
        /** this is ignored by `parse` because it tries multiple formats */
        dateInput: undefined,
    },
    display: {
        dateInput: 'L',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};
// date-fns doesn't provide locale-based start of week
// so we'll just use the ISO standard of Monday
var START_OF_WEEK = 1;
/**
 * Creates an array with all numbers between `start` and `end` (inclusive)
 * with values assigned using the provided `valueFunction`
 *
 * Taken from the `NativeDateAdapter` in `@angular/components`
 */
function range(start, end, valueFunction) {
    var valuesArray = Array(end - start + 1);
    for (var i = start; i <= end; i++) {
        valuesArray[i - start] = valueFunction(i);
    }
    return valuesArray;
}
var DateFnsDateAdapter = /** @class */ (function (_super) {
    __extends(DateFnsDateAdapter, _super);
    function DateFnsDateAdapter(dateFns) {
        var _this = _super.call(this) || this;
        _this.dateFns = dateFns;
        // localized datefns knows the locale from uiConfig
        _this.localeLoaded = _this.dateFns.localePromise.then(function (loadedLocale) {
            // this is a bit hacky because it won't set the `dateFnsLocale` until
            // after the LocalizedDateFns service downloads the appropriate chunk.
            // it's unlikely, but if someone tries to use a datepicker before
            // the locale bundle is downloaded, it will not be localised yet.
            _this.dateFnsLocale = loadedLocale;
        });
        return _this;
    }
    DateFnsDateAdapter.prototype.addCalendarDays = function (date, days) {
        return addDays(date, days);
    };
    DateFnsDateAdapter.prototype.addCalendarMonths = function (date, months) {
        return addMonths(date, months);
    };
    DateFnsDateAdapter.prototype.addCalendarYears = function (date, years) {
        return addYears(date, years);
    };
    DateFnsDateAdapter.prototype.clone = function (date) {
        return toDate(date);
    };
    DateFnsDateAdapter.prototype.createDate = function (year, month, date) {
        // create a definitely valid date with 0 hr 0 min etc.
        var base = new Date(2019, 1, 1);
        // TODO: dream about the pipe operator
        return setDate(setMonth(setYear(base, year), month), date);
    };
    DateFnsDateAdapter.prototype.format = function (date, displayFormat) {
        return format(date, displayFormat, { locale: this.dateFnsLocale });
    };
    DateFnsDateAdapter.prototype.getDate = function (date) {
        return getDate(date);
    };
    DateFnsDateAdapter.prototype.getDateNames = function () {
        var date = new Date(2019, 0, 1);
        return range(1, 31, function (day) { return format(setDate(date, day), 'D'); });
    };
    DateFnsDateAdapter.prototype.getDayOfWeek = function (date) {
        // date-fns doesn't have a getDayOfWeek function
        // we have to use `format` and parse it back into a number
        return parseInt(format(date, 'd'), 10);
    };
    DateFnsDateAdapter.prototype.getDayOfWeekNames = function (style) {
        var _this = this;
        var map = {
            long: 'dddd',
            short: 'ddd',
            narrow: 'dd',
        };
        var formatStr = map[style];
        var date = new Date(2019, 0, 1);
        return range(0, 6, function (day) {
            return format(setDay(date, day, { weekStartsOn: START_OF_WEEK }), formatStr, {
                locale: _this.dateFnsLocale,
            });
        });
    };
    DateFnsDateAdapter.prototype.getFirstDayOfWeek = function () {
        // date-fns doesn't tell us what this is so use the ISO standard of 1
        return START_OF_WEEK;
    };
    DateFnsDateAdapter.prototype.getMonth = function (date) {
        return getMonth(date);
    };
    DateFnsDateAdapter.prototype.getMonthNames = function (style) {
        var _this = this;
        var map = {
            long: 'MMMM',
            short: 'MMM',
            // FIXME: upgrade datefns
            // v2.0.0.alpha.7 narrows months to numbers
            narrow: 'MMM',
        };
        var formatStr = map[style];
        var date = new Date(2019, 0, 1);
        return range(0, 11, function (month) {
            return format(setMonth(date, month), formatStr, {
                locale: _this.dateFnsLocale,
            });
        });
    };
    DateFnsDateAdapter.prototype.getNumDaysInMonth = function (date) {
        return getDaysInMonth(date);
    };
    DateFnsDateAdapter.prototype.getYear = function (date) {
        return getYear(date);
    };
    DateFnsDateAdapter.prototype.getYearName = function (date) {
        return format(date, 'YYYY', {
            locale: this.dateFnsLocale,
        });
    };
    DateFnsDateAdapter.prototype.invalid = function () {
        return new Date(NaN);
    };
    DateFnsDateAdapter.prototype.isDateInstance = function (obj) {
        return obj instanceof Date;
    };
    DateFnsDateAdapter.prototype.isValid = function (date) {
        return date instanceof Date && !Number.isNaN(date.getTime());
    };
    DateFnsDateAdapter.prototype.parse = function (value) {
        // parse can have anything put in it
        if (this.isDateInstance(value) || typeof value === 'number') {
            return toDate(value);
        }
        // empty string case - date-fns will otherwise parse this as Invalid Date
        if (!value) {
            return null;
        }
        // L forces double-digit months (01, 02, etc) which people may not expect
        // so try both 'L' and 'I' to see if either one works
        var res = parse(value, 'L', new Date(), {
            locale: this.dateFnsLocale,
            weekStartsOn: START_OF_WEEK,
        });
        if (this.isValid(res)) {
            return res;
        }
        return parse(value, 'I', new Date(), {
            locale: this.dateFnsLocale,
            weekStartsOn: START_OF_WEEK,
        });
    };
    DateFnsDateAdapter.prototype.deserialize = function (value) {
        /**
         * Matches strings that have the form of a valid RFC 339 string
         * Taken from the `NativeDateAdapter` in `@angular/components`
         */
        var regex = /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|(?:(?:\+|-)\d{2}:\d{2}))?)?$/;
        if (typeof value === 'string') {
            if (!value) {
                return null;
            }
            // The `Date` constructor accepts formats other than ISO 8601, so we need to make sure the
            // string is the right format first.
            if (regex.test(value)) {
                var date = new Date(value);
                if (this.isValid(date)) {
                    return date;
                }
            }
        }
        return _super.prototype.deserialize.call(this, value);
    };
    DateFnsDateAdapter.prototype.toIso8601 = function (date) {
        return date.toISOString();
    };
    DateFnsDateAdapter.prototype.today = function () {
        return new Date();
    };
    return DateFnsDateAdapter;
}(DateAdapter));
export { DateFnsDateAdapter };
